// ============================================================
// TESSERACT — Entry Point (Responsive + Polished)
// ============================================================

import { createGame, currentState, makeGameMove, getMovesFrom, getLegalMoves, undoMove } from '@core/game';
import { type Game, type Move, type UIState, type Side, type GameResult, PLANE_THEMES, BOARD_SIZE, NUM_PLANES, PIECE_SYMBOLS, posEq } from '@core/types';
import { getAt, findKing, getPieces } from '@core/board';
import { isInCheck } from '@core/moves';
import { formatMove } from '@core/notation';
import { screenToBoard } from '@utils/coords';
import { createClassicalAI, type AIEngine } from '@ai/engine';

// --------------- Responsive Layout ---------------

interface Layout {
  cell: number;
  gap: number;
  margin: number;
  cols: number;
  rows: number;
  totalW: number;
  totalH: number;
  origins: Array<{ x: number; y: number }>;
  fontSize: {
    piece: number;
    label: number;
    coord: number;
    hud: number;
  };
}

function computeLayout(): Layout {
  const wrap = document.getElementById('canvas-wrap')!;
  const availW = wrap.clientWidth;
  const availH = wrap.clientHeight;

  if (availW === 0 || availH === 0) {
    return computeLayoutFor(window.innerWidth - 400, window.innerHeight, 2);
  }

  const cols = availW < 500 ? 1 : 2;
  return computeLayoutFor(availW, availH, cols);
}

function computeLayoutFor(availW: number, availH: number, cols: number): Layout {
  const rows = cols === 1 ? 4 : 2;
  const gapRatio = 0.7;
  const marginRatio = 0.5;

  const wFactor = cols * BOARD_SIZE + (cols - 1) * gapRatio + 2 * marginRatio;
  const cellFromW = availW / wFactor;
  const hFactor = rows * BOARD_SIZE + (rows - 1) * gapRatio + 2 * marginRatio;
  const cellFromH = availH / hFactor;

  const cell = Math.max(16, Math.min(64, Math.floor(Math.min(cellFromW, cellFromH))));
  const gap = Math.round(cell * gapRatio);
  const margin = Math.round(cell * marginRatio);

  const totalW = cols * BOARD_SIZE * cell + (cols - 1) * gap + 2 * margin;
  const totalH = rows * BOARD_SIZE * cell + (rows - 1) * gap + 2 * margin;

  const origins: Array<{ x: number; y: number }> = [];
  for (let i = 0; i < NUM_PLANES; i++) {
    const c = i % cols;
    const r = Math.floor(i / cols);
    origins.push({
      x: margin + c * (BOARD_SIZE * cell + gap),
      y: margin + r * (BOARD_SIZE * cell + gap),
    });
  }

  return {
    cell, gap, margin, cols, rows,
    totalW, totalH, origins,
    fontSize: {
      piece: cell * 0.7,
      label: Math.max(9, cell * 0.2),
      coord: Math.max(7, cell * 0.17),
      hud: Math.max(10, cell * 0.22),
    },
  };
}

// --------------- Stats Tracking ---------------

interface GameStats {
  gamesPlayed: number;
  wins: number;
  losses: number;
  draws: number;
  stalemates: number;
  totalMoves: number;
  crossDimMoves: number;
  checksDelivered: number;
  piecesLost: number;
  piecesCaptured: number;
  fastestWin: number | null;       // fewest moves to win
  longestSurvival: number | null;   // most moves before losing
  queenKills: number;              // times you captured a queen
  queenDeaths: number;             // times your queen was captured
  fluxQueenDeaths: number;         // times killed by a cross-dim queen move
  currentStreak: number;           // positive = wins, negative = losses
  bestStreak: number;
}

const DEFAULT_STATS: GameStats = {
  gamesPlayed: 0, wins: 0, losses: 0, draws: 0, stalemates: 0,
  totalMoves: 0, crossDimMoves: 0, checksDelivered: 0,
  piecesLost: 0, piecesCaptured: 0,
  fastestWin: null, longestSurvival: null,
  queenKills: 0, queenDeaths: 0, fluxQueenDeaths: 0,
  currentStreak: 0, bestStreak: 0,
};

function loadStats(): GameStats {
  try {
    const raw = localStorage.getItem('tesseract-stats');
    return raw ? { ...DEFAULT_STATS, ...JSON.parse(raw) } : { ...DEFAULT_STATS };
  } catch { return { ...DEFAULT_STATS }; }
}

function saveStats(stats: GameStats) {
  localStorage.setItem('tesseract-stats', JSON.stringify(stats));
}

function recordGameEnd(result: GameResult | null, moves: Move[], playerSide: Side) {
  const stats = loadStats();
  stats.gamesPlayed++;
  const moveCount = moves.filter(m => m.piece.side === playerSide).length;
  stats.totalMoves += moveCount;

  // Count cross-dim moves, checks, captures
  for (const m of moves) {
    if (m.piece.side === playerSide) {
      if (m.isCrossDimensional) stats.crossDimMoves++;
      if (m.isCheck) stats.checksDelivered++;
      if (m.capture) {
        stats.piecesCaptured++;
        if (m.capture.kind === 'queen') stats.queenKills++;
      }
    } else {
      if (m.capture) {
        stats.piecesLost++;
        if (m.capture.kind === 'queen') stats.queenDeaths++;
        if (m.capture.kind === 'queen' && m.isCrossDimensional) stats.fluxQueenDeaths++;
      }
    }
  }

  if (!result) return;

  if (result.kind === 'checkmate') {
    if (result.winner === playerSide) {
      stats.wins++;
      stats.currentStreak = Math.max(1, stats.currentStreak + 1);
      stats.bestStreak = Math.max(stats.bestStreak, stats.currentStreak);
      if (!stats.fastestWin || moveCount < stats.fastestWin) stats.fastestWin = moveCount;
    } else {
      stats.losses++;
      stats.currentStreak = Math.min(-1, stats.currentStreak - 1);
      if (!stats.longestSurvival || moveCount > stats.longestSurvival) stats.longestSurvival = moveCount;
    }
  } else if (result.kind === 'stalemate') {
    stats.stalemates++;
    stats.currentStreak = 0;
  } else if (result.kind === 'draw') {
    stats.draws++;
    stats.currentStreak = 0;
  }

  saveStats(stats);
  showStatsPanel(stats);
}

function getStatsReport(): string {
  const s = loadStats();
  const winRate = s.gamesPlayed > 0 ? ((s.wins / s.gamesPlayed) * 100).toFixed(1) : '0.0';
  const crossDimRate = s.totalMoves > 0 ? ((s.crossDimMoves / s.totalMoves) * 100).toFixed(1) : '0.0';
  return [
    `🎮 Games: ${s.gamesPlayed} | W:${s.wins} L:${s.losses} D:${s.draws + s.stalemates}`,
    `📊 Win Rate: ${winRate}% | Streak: ${s.currentStreak > 0 ? '+' : ''}${s.currentStreak} (Best: ${s.bestStreak})`,
    `⚔️ Captured: ${s.piecesCaptured} | Lost: ${s.piecesLost}`,
    `👑 Queen Kills: ${s.queenKills} | Queen Deaths: ${s.queenDeaths} | Flux Queen Deaths: ${s.fluxQueenDeaths}`,
    `🌀 Cross-Dim Rate: ${crossDimRate}% | Checks: ${s.checksDelivered}`,
    s.fastestWin ? `⚡ Fastest Win: ${s.fastestWin} moves` : '',
    s.longestSurvival ? `🛡️ Longest Survival: ${s.longestSurvival} moves` : '',
  ].filter(Boolean).join('\n');
}

function showStatsPanel(_stats: GameStats) {
  let panel = document.getElementById('stats-panel');
  if (!panel) {
    panel = document.createElement('div');
    panel.id = 'stats-panel';
    panel.style.cssText = 'position:fixed;bottom:12px;right:12px;background:#0e0e18;border:1px solid #2a2a3a;border-radius:8px;padding:12px 16px;font-size:11px;color:#8888aa;font-family:JetBrains Mono,monospace;z-index:100;max-width:320px;line-height:1.6;white-space:pre-line;';
    document.body.appendChild(panel);
  }
  panel.textContent = getStatsReport();
  setTimeout(() => panel?.remove(), 8000);
}

// Expose stats to console for bots to read
(window as any).TESSERACT_STATS = {
  get: loadStats,
  report: getStatsReport,
  reset: () => { localStorage.removeItem('tesseract-stats'); return 'Stats reset.'; },
};

// --------------- State ---------------

let game: Game = createGame();
let ai: AIEngine = createClassicalAI(3);
let aiThinking = false;
let aiEnabled = true;
let aiSide: Side = 'black';

let ui: UIState = {
  selectedPiece: null,
  validMoves: [],
  hoveredSquare: null,
  activePlane: 0,
  layoutMode: 'grid-2x2',
  animating: false,
};

let moveLog: Move[] = [];
let layout: Layout = computeLayout();

// --------------- Canvas Setup ---------------

const canvas = document.getElementById('game') as HTMLCanvasElement;
const ctx = canvas.getContext('2d')!;

function resize() {
  layout = computeLayout();
  const dpr = window.devicePixelRatio || 1;
  canvas.width = layout.totalW * dpr;
  canvas.height = layout.totalH * dpr;
  canvas.style.width = layout.totalW + 'px';
  canvas.style.height = layout.totalH + 'px';
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
}
resize();

let resizeTimer: number;
window.addEventListener('resize', () => {
  clearTimeout(resizeTimer);
  resizeTimer = window.setTimeout(() => { resize(); render(); }, 60);
});

window.addEventListener('orientationchange', () => {
  setTimeout(() => { resize(); render(); }, 200);
});

// --------------- Piece Rendering ---------------

function drawPiece(cx: number, cy: number, cell: number, side: Side, kind: string, fontSize: number) {
  const sym = PIECE_SYMBOLS[side][kind as keyof typeof PIECE_SYMBOLS['white']];

  // Outer glow for visibility
  if (side === 'black') {
    // Strong white outline for black pieces
    ctx.font = `${fontSize}px serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const px = cx + cell / 2;
    const py = cy + cell / 2;

    // White stroke outline
    ctx.strokeStyle = 'rgba(200,200,220,0.9)';
    ctx.lineWidth = Math.max(2, cell * 0.06);
    ctx.lineJoin = 'round';
    ctx.strokeText(sym, px, py);

    // Main black piece with dark fill
    ctx.fillStyle = '#1a1a2a';
    ctx.fillText(sym, px, py);

    // Inner highlight
    ctx.fillStyle = 'rgba(80,80,120,0.4)';
    ctx.fillText(sym, px, py - 0.5);
  } else {
    // White pieces: bright with subtle shadow
    ctx.font = `${fontSize}px serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const px = cx + cell / 2;
    const py = cy + cell / 2;

    // Drop shadow
    ctx.fillStyle = 'rgba(0,0,0,0.6)';
    ctx.fillText(sym, px + 1, py + 2);

    // Dark outline
    ctx.strokeStyle = 'rgba(40,40,60,0.8)';
    ctx.lineWidth = Math.max(1.5, cell * 0.04);
    ctx.lineJoin = 'round';
    ctx.strokeText(sym, px, py);

    // Main white piece
    ctx.fillStyle = '#f5f5ff';
    ctx.fillText(sym, px, py);

    // Top shine
    ctx.fillStyle = 'rgba(255,255,255,0.3)';
    ctx.fillText(sym, px, py - 0.5);
  }
}

// --------------- Check Attack Lines ---------------

function drawCheckAttackLines(state: ReturnType<typeof currentState>, inCheck: boolean) {
  if (!inCheck) return;

  const { cell, origins } = layout;
  const kingPos = findKing(state, state.activePlayer);
  if (!kingPos) return;

  const opp: Side = state.activePlayer === 'white' ? 'black' : 'white';
  const enemies = getPieces(state, opp);

  // Find pieces attacking the king using pseudo-legal move gen
  for (const { pos: attackerPos } of enemies) {
    const moves = getMovesFrom(game, attackerPos.x, attackerPos.y, attackerPos.w);
    const attacksKing = moves.some(m => posEq(m.to, kingPos));
    if (!attacksKing) continue;

    // Draw attack line from attacker to king
    const aOx = origins[attackerPos.w].x;
    const aOy = origins[attackerPos.w].y;
    const kOx = origins[kingPos.w].x;
    const kOy = origins[kingPos.w].y;

    const ax = aOx + attackerPos.x * cell + cell / 2;
    const ay = aOy + (BOARD_SIZE - 1 - attackerPos.y) * cell + cell / 2;
    const kx = kOx + kingPos.x * cell + cell / 2;
    const ky = kOy + (BOARD_SIZE - 1 - kingPos.y) * cell + cell / 2;

    // Highlight attacker square
    const aCx = aOx + attackerPos.x * cell;
    const aCy = aOy + (BOARD_SIZE - 1 - attackerPos.y) * cell;
    ctx.fillStyle = 'rgba(255,60,60,0.25)';
    ctx.fillRect(aCx, aCy, cell, cell);
    ctx.strokeStyle = 'rgba(255,60,60,0.7)';
    ctx.lineWidth = Math.max(1.5, cell * 0.04);
    ctx.setLineDash([]);
    ctx.strokeRect(aCx + 1, aCy + 1, cell - 2, cell - 2);

    // Draw attack line (dashed red)
    // Only draw if on same plane or close enough to be meaningful
    const samePlane = attackerPos.w === kingPos.w;
    if (samePlane) {
      ctx.beginPath();
      ctx.moveTo(ax, ay);
      ctx.lineTo(kx, ky);
      ctx.strokeStyle = 'rgba(255,50,50,0.6)';
      ctx.lineWidth = Math.max(1.5, cell * 0.04);
      ctx.setLineDash([Math.max(3, cell * 0.1), Math.max(3, cell * 0.08)]);
      ctx.stroke();
      ctx.setLineDash([]);

      // Arrowhead pointing to king
      const angle = Math.atan2(ky - ay, kx - ax);
      const arrowLen = Math.max(6, cell * 0.2);
      ctx.beginPath();
      ctx.moveTo(kx, ky);
      ctx.lineTo(kx - arrowLen * Math.cos(angle - 0.4), ky - arrowLen * Math.sin(angle - 0.4));
      ctx.moveTo(kx, ky);
      ctx.lineTo(kx - arrowLen * Math.cos(angle + 0.4), ky - arrowLen * Math.sin(angle + 0.4));
      ctx.strokeStyle = 'rgba(255,50,50,0.8)';
      ctx.lineWidth = Math.max(1.5, cell * 0.04);
      ctx.stroke();
    } else {
      // Cross-dimensional check: draw a pulsing border on both squares
      // King square already highlighted by check code
      // Add a "⚡" indicator on the attacker
      ctx.font = `${cell * 0.3}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = 'rgba(255,80,80,0.9)';
      ctx.fillText('⚡', aCx + cell * 0.85, aCy + cell * 0.15);
    }
  }
}

// --------------- Rendering ---------------

function render() {
  const state = currentState(game);
  const inCheck = isInCheck(state, state.activePlayer);
  const { cell, origins, fontSize } = layout;

  // Clear
  ctx.fillStyle = '#08080d';
  ctx.fillRect(0, 0, layout.totalW, layout.totalH);

  // Draw each plane
  for (let w = 0; w < NUM_PLANES; w++) {
    const ox = origins[w].x;
    const oy = origins[w].y;
    const theme = PLANE_THEMES[w];
    const boardPx = BOARD_SIZE * cell;

    // Plane glow
    const glowPad = Math.max(3, cell * 0.1);
    ctx.fillStyle = theme.accentGlow;
    ctx.beginPath();
    ctx.roundRect(ox - glowPad, oy - glowPad, boardPx + glowPad * 2, boardPx + glowPad * 2, 4);
    ctx.fill();

    // Label (inside top-left of board)
    ctx.font = `bold ${Math.max(9, fontSize.label)}px "Inter", "Segoe UI", sans-serif`;
    ctx.textAlign = 'left';
    ctx.fillStyle = theme.accent;
    ctx.globalAlpha = 0.7;
    ctx.fillText(theme.label, ox + 3, oy + Math.max(10, fontSize.label) + 2);
    ctx.globalAlpha = 1.0;

    // Cells
    for (let y = 0; y < BOARD_SIZE; y++) {
      for (let x = 0; x < BOARD_SIZE; x++) {
        const drawY = BOARD_SIZE - 1 - y;
        const cx = ox + x * cell;
        const cy = oy + drawY * cell;
        const isLight = (x + y) % 2 === 0;

        // Base cell
        ctx.fillStyle = isLight ? theme.lightCell : theme.darkCell;
        ctx.fillRect(cx, cy, cell, cell);

        // Last move highlight
        if (moveLog.length > 0) {
          const last = moveLog[moveLog.length - 1];
          if ((last.from.x === x && last.from.y === y && last.from.w === w) ||
              (last.to.x === x && last.to.y === y && last.to.w === w)) {
            ctx.fillStyle = 'rgba(240,195,111,0.18)';
            ctx.fillRect(cx, cy, cell, cell);
          }
        }

        // Selected piece
        if (ui.selectedPiece && ui.selectedPiece.x === x && ui.selectedPiece.y === y && ui.selectedPiece.w === w) {
          ctx.fillStyle = 'rgba(124,111,240,0.4)';
          ctx.fillRect(cx, cy, cell, cell);
          ctx.strokeStyle = '#7c6ff0';
          ctx.lineWidth = Math.max(1, cell * 0.035);
          ctx.strokeRect(cx + 1, cy + 1, cell - 2, cell - 2);
        }

        // Valid moves
        const isValidTarget = ui.validMoves.some(m => m.x === x && m.y === y && m.w === w);
        if (isValidTarget) {
          const targetPiece = getAt(state, { x, y, w });
          const isCrossDim = ui.selectedPiece && ui.selectedPiece.w !== w;

          if (targetPiece) {
            ctx.fillStyle = 'rgba(240,80,80,0.3)';
            ctx.fillRect(cx, cy, cell, cell);
            ctx.strokeStyle = 'rgba(240,80,80,0.6)';
            ctx.lineWidth = Math.max(1, cell * 0.035);
            ctx.strokeRect(cx + 1, cy + 1, cell - 2, cell - 2);
          } else if (isCrossDim) {
            ctx.fillStyle = 'rgba(240,111,140,0.25)';
            ctx.fillRect(cx, cy, cell, cell);
            ctx.strokeStyle = '#f06f8c';
            ctx.lineWidth = Math.max(1, cell * 0.025);
            ctx.setLineDash([Math.max(2, cell * 0.07), Math.max(2, cell * 0.07)]);
            ctx.strokeRect(cx + 2, cy + 2, cell - 4, cell - 4);
            ctx.setLineDash([]);
          } else {
            ctx.beginPath();
            ctx.arc(cx + cell / 2, cy + cell / 2, cell * 0.14, 0, Math.PI * 2);
            ctx.fillStyle = 'rgba(124,111,240,0.5)';
            ctx.fill();
          }
        }

        // Hover
        if (ui.hoveredSquare && ui.hoveredSquare.x === x && ui.hoveredSquare.y === y && ui.hoveredSquare.w === w) {
          ctx.fillStyle = 'rgba(255,255,255,0.05)';
          ctx.fillRect(cx, cy, cell, cell);
        }

        // Check highlight on king
        const piece = getAt(state, { x, y, w });
        if (piece && piece.kind === 'king' && piece.side === state.activePlayer && inCheck) {
          // Radial glow behind king
          const grd = ctx.createRadialGradient(
            cx + cell / 2, cy + cell / 2, cell * 0.1,
            cx + cell / 2, cy + cell / 2, cell * 0.7
          );
          grd.addColorStop(0, 'rgba(255,40,40,0.5)');
          grd.addColorStop(1, 'rgba(255,40,40,0)');
          ctx.fillStyle = grd;
          ctx.fillRect(cx - cell * 0.2, cy - cell * 0.2, cell * 1.4, cell * 1.4);

          ctx.fillStyle = 'rgba(255,50,50,0.3)';
          ctx.fillRect(cx, cy, cell, cell);
          ctx.strokeStyle = 'rgba(255,50,50,0.8)';
          ctx.lineWidth = Math.max(2, cell * 0.05);
          ctx.strokeRect(cx + 1, cy + 1, cell - 2, cell - 2);
        }

        // Draw piece with enhanced rendering
        if (piece) {
          drawPiece(cx, cy, cell, piece.side, piece.kind, fontSize.piece);
        }
      }
    }

    // Coordinates
    ctx.font = `${fontSize.coord}px monospace`;
    ctx.fillStyle = '#555568';
    ctx.textAlign = 'center';
    const files = 'abcdefgh';
    for (let x = 0; x < 8; x++) {
      ctx.fillText(files[x], ox + x * cell + cell / 2, oy + boardPx + fontSize.coord + 2);
    }
    ctx.textAlign = 'right';
    for (let y = 0; y < 8; y++) {
      ctx.fillText(String(8 - y), ox - 3, oy + y * cell + cell / 2 + fontSize.coord * 0.35);
    }
  }

  // Draw check attack lines (on top of everything)
  drawCheckAttackLines(state, inCheck);

  // AI thinking indicator
  if (aiThinking) {
    ctx.font = `bold ${fontSize.hud}px "Inter", "Segoe UI", sans-serif`;
    ctx.textAlign = 'center';
    ctx.fillStyle = '#f0c36f';
    ctx.fillText('⟳ AI thinking...', layout.totalW / 2, layout.totalH - 8);
  }

  updateHUD();
}

// --------------- HUD ---------------

function updateHUD() {
  const turnEl = document.getElementById('turn-indicator');
  if (!turnEl) return;

  const s = currentState(game);
  if (game.result) {
    if (game.result.kind === 'checkmate') {
      turnEl.textContent = `♔ Checkmate! ${game.result.winner === 'white' ? 'White' : 'Black'} wins.`;
      turnEl.style.color = '#f06f8c';
    } else if (game.result.kind === 'stalemate') {
      turnEl.textContent = '½ Stalemate — Draw';
      turnEl.style.color = '#f0c36f';
    } else if (game.result.kind === 'draw') {
      turnEl.textContent = `½ Draw — ${game.result.reason}`;
      turnEl.style.color = '#f0c36f';
    }
  } else {
    const check = isInCheck(s, s.activePlayer) ? ' · CHECK!' : '';
    const thinking = aiThinking ? ' · thinking...' : '';
    turnEl.textContent = `${s.activePlayer === 'white' ? '○ White' : '● Black'} · Turn ${s.turn}${check}${thinking}`;
    turnEl.style.color = check ? '#f06f8c' : '#8888aa';
  }

  updateCaptured();
}

function updateCaptured() {
  const el = document.getElementById('captured');
  if (!el) return;

  const whiteCap: string[] = [];
  const blackCap: string[] = [];

  for (const move of moveLog) {
    if (move.capture) {
      const sym = PIECE_SYMBOLS[move.capture.side][move.capture.kind];
      if (move.capture.side === 'white') whiteCap.push(sym);
      else blackCap.push(sym);
    }
  }

  if (whiteCap.length === 0 && blackCap.length === 0) {
    el.innerHTML = '';
    return;
  }

  el.innerHTML = `
    <span style="color:#f0f0f0;letter-spacing:1px;">${blackCap.join('')}</span>
    <span style="color:#666;letter-spacing:1px;">${whiteCap.join('')}</span>
  `;
}

// --------------- Input ---------------

function handleClick(screenX: number, screenY: number) {
  if (aiThinking || game.result) return;

  const state = currentState(game);
  if (aiEnabled && state.activePlayer === aiSide) return;

  const rect = canvas.getBoundingClientRect();
  const sx = (screenX - rect.left) * (layout.totalW / rect.width);
  const sy = (screenY - rect.top) * (layout.totalH / rect.height);

  const clicked = screenToBoard(sx, sy, layout.origins, layout.cell);
  if (!clicked) {
    ui = { ...ui, selectedPiece: null, validMoves: [] };
    render();
    return;
  }

  if (ui.selectedPiece) {
    const moves = getMovesFrom(game, ui.selectedPiece.x, ui.selectedPiece.y, ui.selectedPiece.w);
    const targetMove = moves.find(m => m.to.x === clicked.x && m.to.y === clicked.y && m.to.w === clicked.w);

    if (targetMove) {
      executeMove(targetMove);
      return;
    }
  }

  const piece = getAt(state, clicked);
  if (piece && piece.side === state.activePlayer) {
    const moves = getMovesFrom(game, clicked.x, clicked.y, clicked.w);
    ui = {
      ...ui,
      selectedPiece: clicked,
      validMoves: moves.map(m => m.to),
    };
  } else {
    ui = { ...ui, selectedPiece: null, validMoves: [] };
  }

  render();
}

canvas.addEventListener('click', (e) => handleClick(e.clientX, e.clientY));

canvas.addEventListener('touchend', (e) => {
  e.preventDefault();
  if (e.changedTouches.length > 0) {
    const t = e.changedTouches[0];
    handleClick(t.clientX, t.clientY);
  }
});

canvas.addEventListener('mousemove', (e) => {
  const rect = canvas.getBoundingClientRect();
  const sx = (e.clientX - rect.left) * (layout.totalW / rect.width);
  const sy = (e.clientY - rect.top) * (layout.totalH / rect.height);
  const hovered = screenToBoard(sx, sy, layout.origins, layout.cell);

  if (!ui.hoveredSquare && !hovered) return;
  if (ui.hoveredSquare && hovered && posEq(ui.hoveredSquare, hovered)) return;

  ui = { ...ui, hoveredSquare: hovered };
  render();
});

// --------------- Move Execution ---------------

function executeMove(move: Move) {
  const notated: Move = { ...move, notation: formatMove(move) };
  game = makeGameMove(game, notated);
  moveLog.push(notated);
  addMoveToPanel(notated);
  ui = { ...ui, selectedPiece: null, validMoves: [] };
  render();

  // Record stats on game end
  if (game.result) {
    const playerSide: Side = aiEnabled ? (aiSide === 'black' ? 'white' : 'black') : 'white';
    recordGameEnd(game.result, moveLog, playerSide);
  }

  if (!game.result && aiEnabled && currentState(game).activePlayer === aiSide) {
    triggerAI();
  }
}

async function triggerAI() {
  aiThinking = true;
  render();

  // Small delay so "thinking" renders before blocking
  await new Promise(r => setTimeout(r, 50));

  try {
    const state = currentState(game);
    const move = await ai.think(state);
    const notated: Move = { ...move, notation: formatMove(move) };
    game = makeGameMove(game, notated);
    moveLog.push(notated);
    addMoveToPanel(notated);

    // Record stats if AI just won
    if (game.result) {
      const playerSide: Side = aiSide === 'black' ? 'white' : 'black';
      recordGameEnd(game.result, moveLog, playerSide);
    }
  } catch (err) {
    console.error('AI error:', err);
  } finally {
    aiThinking = false;
    ui = { ...ui, selectedPiece: null, validMoves: [] };
    render();
  }
}

// --------------- Notation Panel ---------------

function addMoveToPanel(move: Move) {
  const panel = document.getElementById('notation-moves');
  if (!panel) return;

  const entry = document.createElement('div');
  entry.className = 'move-entry' + (move.isCrossDimensional ? ' cross-dim' : '') + (move.isCheck ? ' check' : '');

  const turnNum = Math.ceil(move.turn / 2);
  const isWhite = move.piece.side === 'white';
  const prefix = isWhite ? `${turnNum}. ` : `${turnNum}… `;

  entry.textContent = `${prefix}${move.notation}`;
  if (move.capture) entry.title = `Captured ${move.capture.kind}`;

  panel.appendChild(entry);
  panel.scrollTop = panel.scrollHeight;
}

// --------------- Controls ---------------

function setupControls() {
  document.getElementById('btn-new-game')?.addEventListener('click', () => {
    // Record stats before resetting
    if (game.result) {
      recordGameEnd(game.result, moveLog, aiEnabled ? (aiSide === 'black' ? 'white' : 'black') : 'white');
    }
    game = createGame();
    moveLog = [];
    aiThinking = false;
    ui = { ...ui, selectedPiece: null, validMoves: [] };
    const panel = document.getElementById('notation-moves');
    if (panel) panel.innerHTML = '';
    render();
  });

  document.getElementById('btn-undo')?.addEventListener('click', () => {
    if (aiThinking) return;
    // Undo two moves (player + AI) when AI is enabled, one otherwise
    const steps = aiEnabled ? 2 : 1;
    for (let i = 0; i < steps; i++) {
      const prev = undoMove(game);
      if (!prev) break;
      game = prev;
      moveLog.pop();
    }
    ui = { ...ui, selectedPiece: null, validMoves: [] };
    // Rebuild notation panel
    const panel = document.getElementById('notation-moves');
    if (panel) {
      panel.innerHTML = '';
      for (const m of moveLog) addMoveToPanel(m);
    }
    render();
  });

  const toggleAI = document.getElementById('btn-toggle-ai');
  toggleAI?.addEventListener('click', () => {
    aiEnabled = !aiEnabled;
    toggleAI.textContent = aiEnabled ? '🤖 AI: ON' : '👤 AI: OFF';
    toggleAI.className = 'ctrl-btn' + (aiEnabled ? ' active' : '');
    if (aiEnabled && !game.result && currentState(game).activePlayer === aiSide && !aiThinking) {
      triggerAI();
    }
  });

  const depthSelect = document.getElementById('ai-depth') as HTMLSelectElement | null;
  depthSelect?.addEventListener('change', () => {
    ai = createClassicalAI(parseInt(depthSelect.value, 10));
  });

  const toggleLog = document.getElementById('btn-toggle-log');
  const notationPanel = document.getElementById('notation-panel');
  toggleLog?.addEventListener('click', () => {
    notationPanel?.classList.toggle('collapsed');
    setTimeout(() => { resize(); render(); }, 50);
  });

  if (window.innerWidth < 800 && notationPanel) {
    notationPanel.classList.add('collapsed');
  }

  // Stats button
  document.getElementById('btn-stats')?.addEventListener('click', () => {
    showStatsPanel(loadStats());
  });

  // Copy stats to clipboard for sharing
  document.getElementById('btn-copy-stats')?.addEventListener('click', () => {
    const report = getStatsReport();
    const shareText = `TESSERACT 4D Chess — My Stats\nhttps://tesseract-chi.vercel.app/\n\n${report}\n\nCan you survive the Flux Queen? 🐲`;
    navigator.clipboard.writeText(shareText).then(() => {
      const btn = document.getElementById('btn-copy-stats');
      if (btn) { btn.textContent = '✅'; setTimeout(() => btn.textContent = '🏆', 1500); }
    }).catch(() => {
      // Fallback: show in alert
      alert(shareText);
    });
  });
}

// --------------- Init ---------------

setupControls();
render();
console.log(`TESSERACT initialized. ${getLegalMoves(game).length} legal moves. Cell: ${layout.cell}px`);
